// Stage IG-P1-A:Instagram API client(/api/instagram/*)。
//
// 設計書 §10 の紀律:
//   ページ / hook は **必ずこの client 経由**でデータを取る。
//   provider(mock / meta)を意識した分岐は書かない。
//   backend が Provider を切り替えても、この client の型と呼び出しは変わらない。
//
// P1-A は GET のみ。送信 / 状態更新 / 下書き保存は P1-C で追加する。

import { apiFetch } from "./client";
import type {
  CapabilitiesResponse,
  CommentListResponse,
  ConnectionResponse,
  ConversationDetail,
  ConversationListResponse,
  ContentDraft,
  DashboardResponse,
  DraftListResponse,
  InstagramRefreshResponse,
  MediaItem,
  MediaListResponse,
} from "../modules/instagram/types";

/** undefined / 空文字を落として query string を組む。 */
function qs(params: Record<string, string | number | undefined | null>): string {
  const usable = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (usable.length === 0) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of usable) sp.set(k, String(v));
  return `?${sp.toString()}`;
}

export type ListConversationsParams = {
  status?: string;
  q?: string;
  limit?: number;
  cursor?: string | null;
};

export type ListCommentsParams = {
  status?: string;
  mediaId?: string;
  q?: string;
  limit?: number;
  cursor?: string | null;
};

export type ListMediaParams = {
  limit?: number;
  cursor?: string | null;
};

export type GetConversationParams = {
  limit?: number;
  cursor?: string | null;
};

export const instagramApi = {
  /** 画面起動時に取得する capability マップ(設計書 §13)。 */
  capabilities(): Promise<CapabilitiesResponse> {
    return apiFetch<CapabilitiesResponse>("/instagram/capabilities");
  },

  /** 接続状態。未接続でもエラーにならず connection: null が返る。 */
  connection(): Promise<ConnectionResponse> {
    return apiFetch<ConnectionResponse>("/instagram/connections");
  },

  dashboard(): Promise<DashboardResponse> {
    return apiFetch<DashboardResponse>("/instagram/dashboard");
  },

  listConversations(
    params: ListConversationsParams = {},
  ): Promise<ConversationListResponse> {
    return apiFetch<ConversationListResponse>(
      `/instagram/conversations${qs({
        status: params.status,
        q: params.q,
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    );
  },

  getConversation(
    conversationId: string,
    params: GetConversationParams = {},
  ): Promise<ConversationDetail> {
    return apiFetch<ConversationDetail>(
      `/instagram/conversations/${encodeURIComponent(conversationId)}${qs({
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    );
  },

  markConversationRead(conversationId: string): Promise<{ id: string; is_read: boolean }> {
    return apiFetch<{ id: string; is_read: boolean }>(
      `/instagram/conversations/${encodeURIComponent(conversationId)}/read`,
      { method: "POST" },
    );
  },

  sendConversationMessage(
    conversationId: string,
    body: { text?: string; image_url?: string },
  ): Promise<{ ok: boolean; conversation_id: string; messages: unknown[]; message: unknown | null }> {
    return apiFetch<{ ok: boolean; conversation_id: string; messages: unknown[]; message: unknown | null }>(
      `/instagram/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  listComments(params: ListCommentsParams = {}): Promise<CommentListResponse> {
    return apiFetch<CommentListResponse>(
      `/instagram/comments${qs({
        status: params.status,
        media_id: params.mediaId,
        q: params.q,
        limit: params.limit,
        cursor: params.cursor,
      })}`,
    );
  },

  replyComment(commentId: string, body: { text: string }): Promise<{ ok: boolean; parent_comment_id: string; reply: unknown }> {
    return apiFetch<{ ok: boolean; parent_comment_id: string; reply: unknown }>(
      `/instagram/comments/${encodeURIComponent(commentId)}/reply`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  createMediaComment(mediaId: string, body: { text: string }): Promise<{ ok: boolean; comment: unknown }> {
    return apiFetch<{ ok: boolean; comment: unknown }>(
      `/instagram/media/${encodeURIComponent(mediaId)}/comments`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  listMedia(params: ListMediaParams = {}): Promise<MediaListResponse> {
    return apiFetch<MediaListResponse>(
      `/instagram/media${qs({ limit: params.limit, cursor: params.cursor })}`,
    );
  },

  refreshMedia(mediaId: string): Promise<MediaItem> {
    return apiFetch<MediaItem>(
      `/instagram/media/${encodeURIComponent(mediaId)}/refresh`,
      { method: "POST" },
    );
  },

  listDrafts(status?: string): Promise<DraftListResponse> {
    return apiFetch<DraftListResponse>(`/instagram/drafts${qs({ status })}`);
  },

  // ---- 写路径(P1-C) ----

  createDraft(body: { image_url: string; caption: string; content_type?: string }): Promise<ContentDraft> {
    return apiFetch<ContentDraft>("/instagram/drafts", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  updateDraft(
    draftId: string,
    body: { image_url?: string; caption?: string; content_type?: string },
  ): Promise<ContentDraft> {
    return apiFetch<ContentDraft>(`/instagram/drafts/${encodeURIComponent(draftId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  deleteDraft(draftId: string): Promise<{ id: string; deleted: boolean }> {
    return apiFetch<{ id: string; deleted: boolean }>(
      `/instagram/drafts/${encodeURIComponent(draftId)}`,
      { method: "DELETE" },
    );
  },

  deleteMedia(mediaId: string): Promise<{ id: string; deleted: boolean }> {
    return apiFetch<{ id: string; deleted: boolean }>(
      `/instagram/media/${encodeURIComponent(mediaId)}`,
      { method: "DELETE" },
    );
  },

  publishDraft(draftId: string): Promise<ContentDraft> {
    return apiFetch<ContentDraft>(
      `/instagram/drafts/${encodeURIComponent(draftId)}/publish`,
      { method: "POST" },
    );
  },

  refreshCache(body?: {
    targets?: string[];
    analytics_range?: string;
    force?: boolean;
  }): Promise<InstagramRefreshResponse> {
    return apiFetch<InstagramRefreshResponse>("/instagram/refresh", {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
};
